import { doc, getDoc } from 'firebase/firestore'
import { firestoreDb } from '../firebase'

export const PREVIEW_WORKSPACE_ERROR = 'PREVIEW_WORKSPACE_READONLY'

/**
 * Preview-workspace guard for the mobile outbox. Server-side rules already
 * reject writes to preview workspaces, but rejecting at enqueue time means
 * the offline queue never accumulates writes the user cannot ship — the
 * caller can surface a toast immediately instead of waiting for a drain
 * attempt to fail.
 *
 * Uses a cache-first read so an offline caller doesn't stall the enqueue
 * indefinitely; the cache falls back to network when nothing is stored.
 * When the read fails outright (offline + never cached), we let the enqueue
 * proceed — the drain path will then hit the server rule and mark the
 * mutation failed as usual.
 */
const previewCache = new Map<string, { isPreview: boolean; at: number }>()
const CACHE_TTL_MS = 60_000

export const isPreviewWorkspace = async (workspaceId: string): Promise<boolean> => {
  const cached = previewCache.get(workspaceId)
  if (cached && Date.now() - cached.at < CACHE_TTL_MS) return cached.isPreview
  try {
    const snapshot = await getDoc(doc(firestoreDb, 'workspaces', workspaceId))
    const flag = snapshot.exists() && (snapshot.data() as { isPreview?: boolean }).isPreview === true
    previewCache.set(workspaceId, { isPreview: flag, at: Date.now() })
    return flag
  } catch {
    return false
  }
}

export const assertMutableWorkspace = async (workspaceId: string): Promise<void> => {
  if (await isPreviewWorkspace(workspaceId)) {
    const error = new Error('Preview workspaces are read-only.')
    ;(error as Error & { code?: string }).code = PREVIEW_WORKSPACE_ERROR
    throw error
  }
}
